import { useEffect, useRef } from "react";
import { queryClient } from "@/lib/queryClient";
import { getAuthMeQuietOptions } from "@/services/queryOptions/authQueryOptions";
import { useAuthStore } from "@/hooks/useAuthStore";
import { setUsername } from "@/utils/analytics";
import { clearSentryUser, setSentryUser } from "@/config/sentryConfig";
import { getCsrfToken } from "@/lib/csrf";
import { decideRestoreAction } from "@/hooks/restoreSessionDecision";

export function useRestoreAuthSession() {
  const hasRestored = useRef(false);
  const login = useAuthStore((state) => state.login);
  const markUnauthenticatedSessionValidated = useAuthStore((state) => state.markUnauthenticatedSessionValidated);

  useEffect(() => {
    if (hasRestored.current) {
      return;
    }

    hasRestored.current = true;

    const restoreSession = async () => {
      // Base the decision solely on the readable `csrfToken` cookie — the real,
      // hydration-independent session marker. Reading the persisted
      // `isAuthenticated` flag here raced zustand-persist rehydration on the
      // SSR `/xdao` (hydrateRoot) entry and falsely logged users out.
      const action = decideRestoreAction({ hasCsrfToken: Boolean(getCsrfToken()) });

      if (action === "mark-unauthenticated") {
        markUnauthenticatedSessionValidated();
        clearSentryUser();
        return;
      }

      try {
        const data = await queryClient.fetchQuery(getAuthMeQuietOptions());
        login(data.user);
        setUsername(data.user.username);
        setSentryUser(data.user);
      } catch (error: any) {
        const status = error?.response?.status;

        if (status !== 401) {
          console.error("Failed to restore session:", error);
        }

        markUnauthenticatedSessionValidated();
        clearSentryUser();
      }
    };

    restoreSession();
  }, [login, markUnauthenticatedSessionValidated]);
}
